<template>
  <mf-card-container class="mb-4 pa-0 card-container" :title="chartTitle" :description="chartDescription">
    <template v-slot:filters>
      <v-layout v-if="!isSimpleChart" align-center>
        <export-data v-if="useExportFile" :data-fetcher="getDataToExport" :filename="dataForExportCsv.filename" ghost />
        <span class="agrupamento-string pr-2 body-1">Agrupamento por:</span>
        <filter-type-toggle-button :type.sync="filterByDatePeriodComputed" @update:type="(value) => $emit('update:filter-type', value)" />
        <chart-type-toggle-button v-if="!hideChartTypeButton" :type.sync="chartType" />
      </v-layout>
    </template>
    <mf-loading class="pa-0" :loader="loader" :boolean-loader="chartLoading" loader-type="chart">
      <div>
        <slot name="tabs-menu"></slot>
        <apex-chart v-if="existSomeChartSerie" :height="cardHeight" :options="chartConfig" :series="series" />

        <apex-chart-navigator
          v-if="!isSimpleChart && !doNotExistChartSeries"
          :chart-labels-length="allChartLabels.length"
          :interval="navigatorInterval"
          :current-position.sync="navigatorCurrentPosition"
          :filter-type="filterByDatePeriodComputed"
        />
      </div>

      <chart-empty-state v-if="doNotExistChartSeries" />
    </mf-loading>
  </mf-card-container>
</template>

<script>
import capitalize from 'lodash/capitalize'
import VueApexCharts from 'vue-apexcharts'
import { DateTime } from 'luxon'
import { colors } from '@/config/colors.js'
import { abbreviation } from '@/config/helpers.js'
import FilterTypeToggleButton from '@/components/FilterTypeToggleButton.vue'
import ChartTypeToggleButton from '@/components/ChartTypeToggleButton.vue'
import ApexChartNavigator from '@/components/ApexChartNavigator.vue'
import ExportData from './export-data/ExportData.vue'
import ChartEmptyState from './campaigns/ChartEmptyState.vue'
export default {
  name: 'ApexChartCard',

  components: {
    FilterTypeToggleButton,
    ChartTypeToggleButton,
    ApexChart: VueApexCharts,
    ApexChartNavigator,
    ExportData,
    ChartEmptyState,
  },

  props: {
    chartTitle: {
      type: String,
      required: true,
    },
    chartDescription: {
      type: String,
    },
    filterByDatePeriod: {
      type: String,
      required: true,
    },
    configUpdateOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    chartLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSimpleChart: {
      type: Boolean,
      default: false,
    },
    chartData: {
      type: Array,
      required: false,
      default: () => [],
    },
    chartColors: {
      type: Array,
      required: false,
      default: () => [],
    },
    seriesData: {
      type: Array,
      required: false,
      default: () => [
        {
          name: '',
          chartDataPropertyName: '',
        },
      ],
    },
    dataForExportCsv: {
      type: Object,
      required: false,
      default: () => ({
        headers: [],
        data: [],
        filename: '',
      }),
    },
    navigatorInterval: {
      type: [String, Number],
      required: false,
      default: 13,
    },
    useExportFile: {
      type: Boolean,
      required: false,
      default: false,
    },
    loader: {
      type: [String, Array],
      required: false,
      default: '',
    },
    isStacked: {
      type: Boolean,
      default: false,
    },
    cardHeight: {
      type: String,
      default: () => '350',
    },
    hideChartTypeButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chartType: 'bar',
      navigatorCurrentPosition: 0,
    }
  },

  computed: {
    chartConfig() {
      if (this.configUpdateOptions) {
        return { ...this.chartDefaultOptions, ...this.configUpdateOptions }
      }
      return this.chartDefaultOptions
    },

    filterByDatePeriodComputed: {
      get() {
        return this.filterByDatePeriod
      },
      set(value) {
        this.$emit('update:filter-by-date-period', value)
      },
    },

    allChartLabels() {
      if (this.filterByDatePeriodComputed === 'mensal') {
        return this.chartData.map(({ label }) => capitalize(DateTime.fromFormat(label?.[0], 'dd/MM/yyyy').toFormat('MMMM/yyyy')))
      }

      return this.chartData.map(({ label }) => label)
    },

    currentChartData() {
      let startPosition = this.navigatorCurrentPosition
      let endPosition = this.navigatorCurrentPosition + this.navigatorInterval

      if (this.allChartLabels.length < this.navigatorInterval) {
        startPosition = 0
        endPosition = this.allChartLabels.length
      }

      const newChartLabels = this.allChartLabels.slice(startPosition, endPosition)
      const newChartData = this.chartData.slice(startPosition, endPosition)

      this.$emit('update:current-chart-data', { newChartLabels, newChartData })
      return {
        labels: newChartLabels,
        data: newChartData,
      }
    },

    chartDefaultOptions() {
      const baseConfig = {
        chart: {
          type: this.chartType,
          stacked: this.chartType === 'line' ? false : this.isStacked,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          background: '#fff',
        },
        plotOptions: {
          bar: {
            columnWidth: '32px',
            borderRadius: 3,
            dataLabels: {
              position: 'top',
            },
          },
        },
        fill: {
          opacity: 1,
          type: 'solid',
        },
        grid: {
          padding: {
            top: 10,
            right: 10,
            bottom: 10,
            left: 10,
          },
        },
        colors: this.chartColors.length > 0 ? this.chartColors : [...colors],
        legend: {
          horizontalAlign: 'left',
          markers: {
            width: 7,
            height: 7,
            radius: 3.5,
          },
        },
        markers: {
          size: 2,
          colors: this.chartColors.length > 0 ? this.chartColors : [...colors],
          strokeColors: this.chartColors.length > 0 ? this.chartColors : [...colors],
          strokeWidth: 2,
          strokeOpacity: 1,
          strokeDashArray: 0,
          fillOpacity: 1,
          shape: 'circle',
          radius: 2,
          showNullDataPoints: true,
          hover: {
            sizeOffset: 2,
          },
        },
        tooltip: {
          enabled: false,
        },
        dataLabels: {
          enabled: true,
          position: 'top',
          offsetY: this.chartType === 'bar' ? -23 : -7,
          style: {
            fontSize: '12px',
            colors: ['rgba(248, 249, 250, 0.82)'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: 'rgba(0, 0, 0, 0.54)',
            padding: 4,
            borderRadius: 2,
          },
          formatter: this.dataLabelsFormatter,
        },
        stroke: {
          curve: 'smooth',
          colors: this.chartColors.length > 0 ? this.chartColors : [...colors],
          width: this.chartType === 'bar' ? 2 : 3,
        },
        xaxis: {
          categories: this.currentChartData.labels,
          labels: {
            style: {
              colors: '#4B525A',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: this.yaxisFormatter,
          },
        },
      }

      return baseConfig
    },

    dataLabelsFormatter() {
      return (value) => {
        return `R$ ${abbreviation(value, { isCurrency: true })}`
      }
    },

    yaxisFormatter() {
      return function (value) {
        return `R$ ${abbreviation(value, { isCurrency: true })}`
      }
    },

    series() {
      return this.seriesData.map(({ name, chartDataPropertyName, totals, type }) => ({
        name,
        type: type ?? this.chartType,
        data: this.currentChartData.data.map((data) => data?.[chartDataPropertyName]),
        totals: totals,
      }))
    },

    existSomeChartSerie() {
      return this.series.some((serie) => serie?.data.length > 0)
    },

    doNotExistChartSeries() {
      return !this.series.some((serie) => serie?.data.length > 0)
    },
  },
  watch: {
    chartType() {
      this.$emit('change-char-type', this.chartType)
    },
  },

  methods: {
    getDataToExport() {
      const { headers, data } = this.dataForExportCsv
      return { headers, rows: data }
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  & .card-title {
    color: $cinza-d20;
    font-family: 'Lato', sans-serif;
    font-size: 22px;
    font-weight: normal;
  }
}

.empty-chart {
  flex: 1;
  align-self: center;
  text-align: center;
  width: 75%;
  height: 300px;
  color: $cinza-l50;
  margin: 0 auto;
  padding-top: 100px;
}

.export-csv {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.01em;
  color: $marca-0-l40 !important;
  text-transform: none;
  -webkit-text-decoration-line: none;
  text-decoration-line: none;
}
</style>
